import {
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select as MuiSelect,
  SelectProps,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export const Select = <T extends string | number>({
  value,
  options,
  label,
  subLabel,
  loading,
  onChange,
  fullWidth = true,
  errorMsg,
  required,
  anyOption = false,
  ...rest
}: Omit<SelectProps, "onChange"> & {
  options?: { value: T; label: string }[];
  subLabel?: string;
  loading?: boolean;
  errorMsg?: string;
  anyOption?: boolean;
  onChange?: (value?: T) => void;
}) => {
  const { t } = useTranslation();

  return (
    <FormControl fullWidth={fullWidth} error={!!errorMsg}>
      <FormLabel
        required={required}
        htmlFor={rest.name}
        sx={{ color: subLabel ? "text.primary" : "text.secondary" }}
      >
        {label}
      </FormLabel>
      {subLabel && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {subLabel}
        </Typography>
      )}
      <MuiSelect
        labelId={`label-${label}`}
        value={value ?? -1}
        size="medium"
        IconComponent={!loading ? ExpandMoreIcon : Loader}
        MenuProps={{
          //Uncomment if you want to keep dropdown always on input bottom
          //   marginThreshold: null,
          //   disableScrollLock: true,
          slotProps: {
            paper: {
              style: {
                maxHeight: 300,
              },
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
        }}
        onChange={(e) =>
          onChange?.(e.target.value !== -1 ? (e.target.value as T) : undefined)
        }
        {...rest}
      >
        {anyOption && <MenuItem value={-1}>{t("common.any")}</MenuItem>}
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
    </FormControl>
  );
};

const Loader = () => (
  <CircularProgress
    data-testid="select-loading-indicator"
    size={20}
    sx={{ position: "absolute", right: "10px" }}
  />
);
